import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import useTranslations from "@/shared/lang/useTranslations";

import type { CardAugmented } from "../constants/cardMocks.constants";
import { useOrgDesign } from "../hooks/useOrgDesign";
import { normalizeProduct, normalizeStatus, type CardStatus } from "../utils/cardHelpers";
import { CardPreviewVisual } from "./CardPreviewVisual";

type CardTileProps = {
  card: CardAugmented;
  onOpen: (cardId: string) => void;
};

const STATUS_VARIANT: Record<
  CardStatus,
  {
    variant: "success" | "warning" | "destructive" | "secondary" | "default";
    tKey: string;
  }
> = {
  ACTIVE: { variant: "success", tKey: "cards_status_active" },
  FROZEN: { variant: "secondary", tKey: "cards_status_frozen" },
  DISABLED: { variant: "destructive", tKey: "cards_status_disabled" },
  DELETED: { variant: "destructive", tKey: "cards_status_deleted" },
  PENDING_REVIEW: { variant: "warning", tKey: "cards_status_pending_review" },
};

const formatUSD = (raw: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(raw);

export const CardTile = ({ card, onOpen }: CardTileProps) => {
  const { t } = useTranslations("cards");
  const design = useOrgDesign();

  const status = normalizeStatus(card.status || card.card_status);
  const product = normalizeProduct(card.card_type || card.product);
  const statusMeta = STATUS_VARIANT[status];
  const pocket = card.pocketRef;
  const last4 = card.last4 || card.last_4 || "0000";
  const holder = card.holder || card.card_holder_name || card.name || "—";
  const alias = card.alias?.trim() || `•••• ${last4}`;
  const available = card.balanceMock;

  return (
    <button
      type="button"
      onClick={() => onOpen(card.id)}
      className="flex flex-col gap-3.5 rounded-[var(--radius-lg)] border border-border bg-card p-4 text-left transition-all hover:border-ring/40 hover:shadow-[var(--shadow-elev)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
    >
      <CardPreviewVisual
        alias={alias}
        last4={last4}
        type={product}
        frozen={card.frozen}
        disabled={card.disabled}
        holder={holder}
        size="md"
        className="mx-auto"
        design={design}
      />

      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-foreground">{alias}</div>
          <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
            {pocket && (
              <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: pocket.color }} />
            )}
            <span className="truncate">{pocket ? `${pocket.name} · ${holder}` : holder}</span>
            <span className="shrink-0 font-mono font-medium tracking-wider">{last4}</span>
          </div>
        </div>
        <ColocaBadge variant={statusMeta.variant}>{t(statusMeta.tKey as never)}</ColocaBadge>
      </div>

      <div className="grid grid-cols-2 gap-3 border-t border-dashed border-border pt-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            {pocket ? t("cards_label_balance_pocket") : t("cards_label_balance_card")}
          </div>
          <div className="text-[15px] font-semibold tabular-nums text-foreground">{formatUSD(available)}</div>
        </div>
        <div className="text-right">
          <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            {t("cards_label_month_spend")}
          </div>
          <div className="font-mono text-sm tabular-nums text-foreground">{formatUSD(card.monthSpend)}</div>
        </div>
      </div>
    </button>
  );
};
